import React, { useState } from "react";
import {
  Menu,
  MenuItem,
  Fab,
  Snackbar,
  Alert,
  Typography,
  Divider,
  Box,
  TextField,
  Button,
} from "@mui/material";
import BugReportIcon from "@mui/icons-material/BugReport";
import copy from "copy-to-clipboard";
import { useOutlookPlugin } from "../hooks/useOutlookPlugin";

export interface OutlookDebugMenuUser {
  name?: string;
  givenName?: string;
  email?: string;
}

export interface OutlookDebugMenuProps {
  // Optional auth integration
  isAuthenticated?: boolean;
  user?: OutlookDebugMenuUser | null;
  onLogout?: () => Promise<void>;
}

export const OutlookDebugMenu: React.FC<OutlookDebugMenuProps> = ({
  isAuthenticated = false,
  user = null,
  onLogout,
}) => {
  const { currentMessageId, currentSharedInbox, isReady, isDevMode, setMockMessageId, setMockSharedInbox, overrideShipmentId, setOverrideShipmentId, actions, contextType } = useOutlookPlugin();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [mockIdInput, setMockIdInput] = useState("");
  const [mockSharedInboxInput, setMockSharedInboxInput] = useState("");
  const [overrideShipmentIdInput, setOverrideShipmentIdInput] = useState("");
  const [openEmailIdInput, setOpenEmailIdInput] = useState("");

  const isOnShipmentTimelinePage = window.location.hash.includes("shipmentTimelineAndAutomation");

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCopyContext = () => {
    // Safely access Office global (may not exist in web context)
    const officeGlobal = typeof window !== "undefined" ? (window as any).Office : undefined;

    const contextData = {
      outlookPluginState: {
        contextType,
        currentMessageId,
        currentSharedInbox,
        isReady,
        isDevMode,
      },
      officeJsInfo: {
        available: !!officeGlobal,
        version: officeGlobal?.context?.platform ?? "Not available",
        host: officeGlobal?.context?.host ?? "Not available",
      },
      environment: {
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        windowLocation: {
          hostname: window.location.hostname,
          href: window.location.href,
        },
      },
    };

    const jsonString = JSON.stringify(contextData, null, 2);
    console.log("Debug context:", jsonString);

    try {
      const success = copy(jsonString, {
        debug: true,
        message: "Press #{key} to copy",
      });

      if (success) {
        console.log("Context copied to clipboard successfully");
        setSnackbarOpen(true);
      } else {
        // Manual fallback
        const textArea = document.createElement("textarea");
        textArea.value = jsonString;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.select();
        textArea.setSelectionRange(0, 99999);
        const fallbackSuccess = document.execCommand("copy");
        document.body.removeChild(textArea);

        if (fallbackSuccess) {
          setSnackbarOpen(true);
        } else {
          alert("Failed to copy to clipboard");
        }
      }
    } catch (error) {
      console.error("Error during copy:", error);
      alert("Failed to copy to clipboard");
    }

    handleClose();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleLogout = async () => {
    if (onLogout) {
      try {
        await onLogout();
        handleClose();
      } catch (error) {
        console.error('Logout failed:', error);
      }
    }
  };

  return (
    <>
      <Fab
        size="small"
        onClick={handleClick}
        style={{
          position: "fixed",
          bottom: 2,
          right: 2,
          zIndex: 1000,
          width: isDevMode ? 48 : 24,
          height: isDevMode ? 48 : 24,
          minHeight: isDevMode ? 48 : 24,
          backgroundColor: isDevMode ? "rgba(255, 152, 0, 0.2)" : "transparent",
          opacity: isDevMode ? 1 : 0.6,
        }}
        title="Debug Menu"
      >
        <BugReportIcon style={{ fontSize: isDevMode ? 24 : 12 }} />
      </Fab>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        {isAuthenticated && user && (
          <>
            <Box sx={{ px: 2, py: 1, minWidth: 200 }}>
              <Typography variant="caption" color="textSecondary" display="block">
                Signed in as
              </Typography>
              <Typography variant="body2" fontWeight="medium" sx={{ wordBreak: 'break-all' }}>
                {user.givenName || user.name || "User"}
              </Typography>
              {user.email && (
                <Typography variant="caption" color="textSecondary" sx={{ wordBreak: 'break-all' }}>
                  {user.email}
                </Typography>
              )}
            </Box>
            <Divider />
            {onLogout && <MenuItem onClick={handleLogout}>Sign Out</MenuItem>}
            <Divider />
          </>
        )}

        <Box sx={{ px: 2, py: 1, minWidth: 200 }}>
          <Typography variant="caption" color="textSecondary" display="block">
            Context: {contextType}
          </Typography>
        </Box>
        <Divider />

        <MenuItem onClick={handleCopyContext}>Copy Context</MenuItem>
        <MenuItem onClick={() => { window.location.hash = "#/products/"; handleClose(); }}>Change Product</MenuItem>

        <Divider />
        <Box sx={{ px: 2, py: 1, minWidth: 200 }}>
          <Typography
            variant="caption"
            color="textSecondary"
            display="block"
            sx={{ mb: 1 }}
          >
            Mock Email Controls
          </Typography>
          <TextField
            size="small"
            fullWidth
            label="Message ID"
            value={mockIdInput}
            onChange={(e) => setMockIdInput(e.target.value)}
            placeholder="Enter message ID"
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                setMockMessageId(mockIdInput || null);
              }}
              disabled={!mockIdInput.trim()}
            >
              Set
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setMockMessageId(null);
                setMockIdInput("");
              }}
            >
              Clear
            </Button>
          </Box>
          {currentMessageId && (
            <Typography
              variant="caption"
              color="success.main"
              display="block"
              sx={{ mt: 1, wordBreak: "break-all" }}
            >
              Current: {currentMessageId}
            </Typography>
          )}

          <Divider sx={{ my: 1 }} />

          <Typography
            variant="caption"
            color="textSecondary"
            display="block"
            sx={{ mb: 1 }}
          >
            Mock Shared Inbox
          </Typography>
          <TextField
            size="small"
            fullWidth
            label="Shared Inbox Email"
            value={mockSharedInboxInput}
            onChange={(e) => setMockSharedInboxInput(e.target.value)}
            placeholder="e.g., shared@company.com"
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                setMockSharedInbox(mockSharedInboxInput || null);
              }}
              disabled={!mockSharedInboxInput.trim()}
            >
              Set
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setMockSharedInbox(null);
                setMockSharedInboxInput("");
              }}
            >
              Clear
            </Button>
          </Box>
          {currentSharedInbox && (
            <Typography
              variant="caption"
              color="success.main"
              display="block"
              sx={{ mt: 1, wordBreak: "break-all" }}
            >
              Shared Inbox: {currentSharedInbox}
            </Typography>
          )}

          {isOnShipmentTimelinePage && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                sx={{ mb: 1 }}
              >
                Override Shipment ID
              </Typography>
              <TextField
                size="small"
                fullWidth
                label="Shipment ID"
                value={overrideShipmentIdInput}
                onChange={(e) => setOverrideShipmentIdInput(e.target.value)}
                placeholder="Enter shipment feed ID"
                sx={{ mb: 1 }}
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    setOverrideShipmentId(overrideShipmentIdInput || null);
                  }}
                  disabled={!overrideShipmentIdInput.trim()}
                >
                  Set
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setOverrideShipmentId(null);
                    setOverrideShipmentIdInput("");
                  }}
                >
                  Clear
                </Button>
              </Box>
              {overrideShipmentId && (
                <Typography
                  variant="caption"
                  color="success.main"
                  display="block"
                  sx={{ mt: 1, wordBreak: "break-all" }}
                >
                  Override: {overrideShipmentId}
                </Typography>
              )}

            </>
          )}

          <Divider sx={{ my: 1 }} />
          <Typography
            variant="caption"
            color="textSecondary"
            display="block"
            sx={{ mb: 1 }}
          >
            Actions
          </Typography>
          <TextField
            size="small"
            fullWidth
            label="Message ID"
            value={openEmailIdInput}
            onChange={(e) => setOpenEmailIdInput(e.target.value)}
            placeholder="Enter message ID to open"
            sx={{ mb: 1 }}
          />
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              if (openEmailIdInput.trim()) {
                actions.openEmail(openEmailIdInput.trim());
              }
            }}
            disabled={!openEmailIdInput.trim()}
          >
            Open Email
          </Button>
        </Box>
      </Menu>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          variant="filled"
        >
          Context copied to clipboard!
        </Alert>
      </Snackbar>
    </>
  );
};
